import { Component, Input, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { ConfirmDialogService } from '../../service/common/confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../service/common/notification/notification.service';
import { Router } from '@angular/router';
import { RoutingService } from '../../service/common/routing/routing.service';
import { ErzeugungsanlageService } from '../../service/erzeugungsanlage/erzeugungsanlage.service';
import { NgForm, NgModel } from '@angular/forms';
import { Anlagentyp } from '../../domain/model/anlagentyp';
import { Observable } from 'rxjs/internal/Observable';
import { Netzzuordnung } from '../../domain/model/netzzuordnung';
import { Erzeugungsanlage } from '../../domain/model/erzeugungsanlage';
import { take, tap } from 'rxjs/operators';
import { ErzeugungsanlagenRefreshService } from '../../service/erzeugungsanlage/erzeugungsanlagen-refresh.service';
import { WestnetzSidenavService } from '../../service/common/westnetz-sidenav/westnetz-sidenav.service';
import { lastValueFrom } from 'rxjs';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';

@Component({
    selector: 'tnb-erzeugungsanlage-detail-summenerzeuger',
    templateUrl: './erzeugungsanlage-detail-summenerzeuger.component.html',
    styleUrls: [
        './erzeugungsanlage-detail-summenerzeuger.component.scss',
    ],
    standalone: false,
})
export class ErzeugungsanlageDetailSummenerzeugerComponent implements OnInit {
    private erzeugungsanlageService = inject(ErzeugungsanlageService);
    private erzeugungsanlageRefreshService = inject(ErzeugungsanlagenRefreshService);
    private routingService = inject(RoutingService);
    private confirmDialogService = inject(ConfirmDialogService);
    private notificationService = inject(NotificationService);
    private router = inject(Router);
    private westnetzSidenavService = inject(WestnetzSidenavService);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    @Input() public erzeugungsanlage: Erzeugungsanlage;
    @Input() public createNewMode = false;
    @Input() public anlagentypen: Observable<Anlagentyp[]>;
    @Input() public readonly = false;

    @ViewChild('erzeugungsanlageForm', { static: true }) public erzeugungsanlageForm: NgForm;
    @ViewChild('abregelbareLeistungKW', { static: false }) public abregelbareLeistungKW: NgModel;

    public stadtwerkeId: string = null;

    private saveOrCreateButtonClicked = false;

    get showDeleteButton(): boolean {
        return !this.readonly && !this.createNewMode &&
            this.stadtwerkeId === this.erzeugungsanlage?.netzzuordnung?.netzbetreiber?.stadtwerkeId;
    }

    public static createNewSummenerzeuger(): Erzeugungsanlage {
        const erzeugungsanlage = new Erzeugungsanlage();
        erzeugungsanlage.netzzuordnung = new Netzzuordnung();
        return erzeugungsanlage;
    }

    ngOnInit() {
        this.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
    }

    public resetForm() {
        this.erzeugungsanlageService.getErzeugungsanlage(this.erzeugungsanlage.westnetzId)
            .pipe(take(1))
            .subscribe(ea => {
                this.erzeugungsanlage = ea;
                this.erzeugungsanlageForm.form.markAsPristine();
            });
    }

    public async saveOrCreateErzeugungsanlage() {

        this.saveOrCreateButtonClicked = true;

        this.erzeugungsanlageService.getErzeugungsanlageWithAnlagentypAndNetzzuordnung(
            this.erzeugungsanlage.anlagentyp.nummer,
            this.erzeugungsanlage.netzzuordnung.netzzuordnungId,
            this.stadtwerkeId)
            .pipe(take(1))
            .subscribe((anlage: Erzeugungsanlage) => {
                if (anlage && anlage.westnetzId !== this.erzeugungsanlage.westnetzId) {
                    this.confirmDialogService.confirm(
                        'Der Summenerzeuger mit der Westnetz-ID ' + anlage.westnetzId + ' besitzt bereits die gleiche Kombination aus ' +
                        'Anlagentyp/Primärenergieträger und Netzzuordnung. Wollen Sie stattdessen jenen Summenerzeuger editieren?',
                        'Gleicher Anlagentyp bereits vorhanden',
                        'warn',
                        'Ja, zum anderen Erzeuger wechseln ',
                        'Nein, diesen weiter editieren',
                        'innogy_picto_arrow-right',
                    ).then(val => {
                        if (val) {
                            this.router.navigateByUrl(this.routingService.erzeugungsanlageByWestnetzId(anlage.westnetzId));
                        } else {
                            this.saveOrCreateButtonClicked = false;
                            this.changeDetectorRef.markForCheck();
                        }
                    });
                } else {
                    (this.createNewMode ?
                        this.erzeugungsanlageService.createSummenerzeuger(
                            this.teilgebietSelectionService.getSelectedStadtwerkeId(),
                            this.erzeugungsanlage,
                        ).pipe(
                            tap(() => this.notificationService.success('Neue Erzeugungsanlage wurde angelegt.')),
                        ) :
                        this.erzeugungsanlageService.saveSummenerzeuger(
                            this.teilgebietSelectionService.getSelectedStadtwerkeId(),
                            this.erzeugungsanlage,
                        ).pipe(
                            tap(() => this.notificationService.success('Ausgewählte Erzeugungsanlage wurde geändert.')),
                        )
                    ).pipe(take(1))
                        .subscribe(() => {
                            this.erzeugungsanlageRefreshService.requestRefresh();
                            this.closeSideNav();
                        });
                }
            });
    }

    // noinspection JSMethodCanBeStatic
    public compareAnlagentyp(a: Anlagentyp, b: Anlagentyp) {
        return (a && b && a.name === b.name);
    }

    public isSaveButtonDisabled(): boolean {
        return !this.erzeugungsanlageForm.form.valid ||
            this.saveOrCreateButtonClicked ||
            this.erzeugungsanlageForm.form.pristine ||
            this.erzeugungsanlage.netzzuordnung == null;
    }

    public isCreateButtonDisabled(): boolean {
        return !this.erzeugungsanlageForm.form.valid ||
            this.saveOrCreateButtonClicked ||
            this.erzeugungsanlage.netzzuordnung == null;
    }

    public async deleteErzeugungsanlage() {
        if (await this.confirmDialogService.confirm(
            'Möchten Sie diese Anlage wirklich löschen?',
            'Anlage löschen',
            'warn',
            'Löschen',
            'Abbrechen',
            'innogy_picto_bin_c-check',
        )) {
            await lastValueFrom(this.erzeugungsanlageService.deleteErzeugungsanlage(this.erzeugungsanlage.westnetzId));

            this.notificationService.success('Ausgewählte Erzeugungsanlage wurde gelöscht.');
            this.erzeugungsanlageRefreshService.requestRefresh();
            this.closeSideNav();
        }
    }

    closeSideNav() {
        this.westnetzSidenavService.closeRightnav();
        this.router.navigate([
            this.routingService.erzeugungsanlagePath(),
        ], {
            queryParams: { erzeugungsanlage: null, createnew: null },
            queryParamsHandling: 'merge',
        });
    }
}
